import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Sparkles } from "@react-three/drei";
import * as THREE from "three";

const ParticleSphere = ({ count = 1800 }) => {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 2.6 + Math.random() * 0.6;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);

  useFrame((_, d) => {
    if (ref.current) ref.current.rotation.y += d * 0.03;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.022}
        color="#C5A059"
        transparent
        opacity={0.85}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
};

const OracleCore = ({ mouse }) => {
  const grp = useRef();
  useFrame((_, d) => {
    const g = grp.current;
    if (!g) return;
    g.rotation.y += d * 0.15;
    g.rotation.x = THREE.MathUtils.lerp(g.rotation.x, mouse.current.y * 0.32, 0.05);
    g.rotation.z = THREE.MathUtils.lerp(g.rotation.z, -mouse.current.x * 0.22, 0.05);
  });

  return (
    <group ref={grp}>
      <mesh>
        <icosahedronGeometry args={[1.35, 1]} />
        <meshBasicMaterial color="#C5A059" wireframe transparent opacity={0.45} />
      </mesh>
      <mesh scale={0.92}>
        <icosahedronGeometry args={[0.9, 0]} />
        <meshStandardMaterial
          color="#3B2A48"
          emissive="#3B2A48"
          emissiveIntensity={0.7}
          roughness={0.25}
          metalness={0.9}
        />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[2, 0.012, 16, 140]} />
        <meshBasicMaterial color="#C5A059" transparent opacity={0.6} />
      </mesh>
      <mesh rotation={[Math.PI / 2.4, 0.5, 0]}>
        <torusGeometry args={[2.4, 0.006, 16, 140]} />
        <meshBasicMaterial color="#C5A059" transparent opacity={0.35} />
      </mesh>
    </group>
  );
};

export const CosmicPortal = ({ mouse }) => {
  return (
    <>
      <ambientLight intensity={0.5} />
      <pointLight position={[4, 4, 4]} intensity={45} color="#C5A059" />
      <pointLight position={[-4, -2, -4]} intensity={22} color="#3B2A48" />
      <ParticleSphere />
      <OracleCore mouse={mouse} />
      <Sparkles count={70} scale={10} size={2.2} speed={0.28} color="#C5A059" opacity={0.5} />
    </>
  );
};

export default CosmicPortal;
